import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import uuid from 'uuid/v4'
import jwtDecode from 'jwt-decode'
import { appBasePath } from '../../../utils/appBasePath'
import settings from '../settings'

export const AuthNobiaContext = createContext({
  token: '',
  loggedIn: false,
  login: () => Promise.resolve(),
})

export const useAuthNobia = () => useContext(AuthNobiaContext)

const parseToken = (data) => ({
  ...data,
  payload: jwtDecode(data.access_token),
})

const getToken = async () => {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const provider = params.get('provider')
  if (provider !== settings.azureAuthorization.provider) {
    return { success: false, error: 'invalid provider returned' }
  }

  const response = await fetch(settings.azureAuthorization.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      provider: settings.azureAuthorization.provider,
      useTokens: false,
    }),
  })

  const data = response.ok && (await response.json().then(parseToken))
  const error = !response.ok && (await response.text())
  return { success: response.ok, data, error }
}

const refreshToken = async ({ refreshToken: token }) => {
  const response = await fetch(settings.azureAuthorization.refreshEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken: token,
      provider: settings.azureAuthorization.provider,
      useTokens: false,
    }),
  })

  const data = response.ok && (await response.json().then(parseToken))
  const error = !response.ok && (await response.text())
  return { success: response.ok, data, error }
}

const redirectAuthorize = (embedded) =>
  new Promise((resolve, reject) => {
    const params = new URLSearchParams()
    params.append('provider', settings.azureAuthorization.provider)
    params.append('state', uuid())
    params.append(
      'callbackUri',
      `${window.location.origin}${appBasePath}login-callback`
    )
    const url = `${
      settings.azureAuthorization.authorizeEndpoint
    }?${params.toString()}`

    const win = window.open(url, 'Login', 'popup,width=700,height=700')

    if (!win || win.closed || typeof win.closed === 'undefined') {
      if (!embedded) {
        window.location.href = url
        return
      }
      reject('Popup blocked')
    }

    const cb = (e) => {
      if (e.source !== win) {
        return
      }
      window.removeEventListener('message', cb)
      resolve(e.data)
    }
    window.addEventListener('message', cb, true)
  })

export const AuthNobiaProvider = ({ children }) => {
  const location = useLocation()
  const history = useHistory()
  const [tokenData, setTokenData] = useState(
    JSON.parse(
      localStorage.getItem(settings.azureAuthorization.storageTokenKey) || '{}'
    )
  )
  const tokenUser = useMemo(() => {
    if (!tokenData.payload || !tokenData.payload.upn) {
      return {}
    }

    return {
      email: tokenData.payload.upn,
    }
  }, [tokenData.payload])

  const isCallback = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const provider = params.get('provider')
    const state = params.get('state')
    const code = params.get('code')
    return code && state && provider
  }, [location.search])

  useEffect(() => {
    const update = (evt) => {
      if (evt.key !== settings.azureAuthorization.storageTokenKey) {
        return
      }

      setTokenData(JSON.parse(evt.newValue || '{}'))
    }
    window.addEventListener('storage', update)
    return () => window.removeEventListener('storage', update)
  }, [])

  useEffect(() => {
    const data = JSON.stringify(tokenData)
    const existing = localStorage.getItem(
      settings.azureAuthorization.storageTokenKey
    )
    if (data !== existing) {
      localStorage.setItem(settings.azureAuthorization.storageTokenKey, data)
    }
  }, [tokenData])

  useEffect(() => {
    const payload = tokenData.payload
    if (!payload || !payload.exp) {
      return () => {}
    }
    const untilRefresh =
      (payload.exp - Math.random() * 100) * 1000 - new Date().getTime()
    const time = setTimeout(async () => {
      const { success, data, error } = await refreshToken({
        refreshToken: tokenData.refresh_token,
      })
      if (success) {
        setTokenData(data)
      } else {
        // eslint-disable-next-line no-console
        console.error(error)
        setTokenData({})
      }
    }, untilRefresh)
    return () => clearTimeout(time)
  }, [tokenData.payload, tokenData.refresh_token])

  const onReturn = useCallback(() => {
    const returnUrl =
      window.localStorage.getItem(
        settings.azureAuthorization.storageReturnUrlKey
      ) || '/'
    window.localStorage.removeItem(
      settings.azureAuthorization.storageReturnUrlKey
    )
    return returnUrl
  }, [])

  const login = useCallback(() => {
    setTokenData({})
    window.localStorage.setItem(
      settings.azureAuthorization.storageReturnUrlKey,
      `${location.pathname}?${location.search}`
    )
    return redirectAuthorize(!!window.parent).then(({ data }) => {
      setTokenData(data)
      onReturn()
    })
  }, [location.pathname, location.search, onReturn])

  const callback = useCallback(async () => {
    const { success, data, error } = await getToken()

    if (window.opener) {
      window.opener.postMessage(
        { success, data: data || {}, error },
        window.location.origin
      )
      window.close()
    } else {
      if (success) {
        setTokenData(data)
      } else {
        // eslint-disable-next-line no-console
        console.error(error)
        setTokenData({})
      }
      const returnUrl = onReturn()
      history.replace(returnUrl)
    }
  }, [history, onReturn])

  useEffect(() => {
    if (!isCallback || tokenData.access_token) {
      return
    }
    callback()
  }, [isCallback, callback, tokenData.access_token])

  const value = useMemo(
    () => ({
      login,
      token: tokenData.access_token,
      loggedIn: !!tokenData.access_token,
      user: tokenUser,
    }),
    [login, tokenData.access_token]
  )

  return (
    <AuthNobiaContext.Provider value={value}>
      {!isCallback && children}
    </AuthNobiaContext.Provider>
  )
}
